import {
  Button,
  Col,
  Flex,
  Form,
  FormInstance,
  Image,
  Input,
  List,
  message,
  Popconfirm,
  Row,
  Spin,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Icon from '@ant-design/icons';
import AddStep from '../../features/template/form/addStep';
import { SectionBlackTitle, Hint } from '../../features/template/form/style';
import CauseCard from '../causeCard';
import ImageUpload from '../imageUpload';
import SelectCauseOrIssueDrawer from '../selectCauseOrIssueDrawer';
import StepCard from '../stepCard';
import SignsField from '../signsField';
import { parseSignNames } from '../../constants';
import { DeleteButton, ImageContainer, Section } from './styles';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  fetchTemplateCauses,
  fetchTemplateRoutines,
  selectDraftTemplate,
  selectTemplateCurrentCauses,
} from '../../slices/templates';
import { useAppDispatch } from '../../store';
import {
  Step,
  Routine,
  Cause,
  ActiveStatus,
  YesOrNo,
  SuggestionStatus,
  UploadBusinessType,
  File,
} from '../../types';
import { useTranslation } from 'react-i18next';
import { createRoutine, updateRoutine } from '../../utils/httpRequest';
import { selectUserId, selectUsername } from '../../slices/profile';
import { SVGUnlink } from '../../images';

const RoutineForm = ({
  form,
  isSuggestion,
  readOnly,
  initialRoutine,
  onSubmit,
  onCancel,
  showFullScreenSpin,
  isSuggestionReview,
  onReview,
  templateID,
  onChange,
}: {
  templateID?: string;
  form?: FormInstance;
  isSuggestion?: boolean;
  readOnly?: boolean;
  initialRoutine?: Routine;
  showFullScreenSpin?: boolean;
  onSubmit?: (routine: Routine) => Promise<void> | void;
  onCancel?: () => void;
  isSuggestionReview?: boolean;
  onReview?: (routine: Routine) => void;
  onChange?: (routine: Routine) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [messageApi, contextHolder] = message.useMessage();

  const draftTemplate = useSelector(selectDraftTemplate);
  const availableCauses = useSelector(selectTemplateCurrentCauses);
  const userId = useSelector(selectUserId);
  const username = useSelector(selectUsername);

  const [isLinkCausesDrawerShow, setIsLinkCausesDrawerShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isStepFormShow, setIsStepFormShow] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const imagesRef = useRef<File[]>([]);
  const [routine, setRoutine] = useState<Routine>();
  const currentStep = useRef<number>(-1);

  useEffect(() => {
    currentStep.current = -1;
    setIsStepFormShow(false);
    setIsLinkCausesDrawerShow(false);
    setRoutine(initialRoutine);
    if (!readOnly) {
      if (initialRoutine) {
        form?.setFieldsValue(initialRoutine);
      } else {
        form?.resetFields();
      }
    }

    const newImages = initialRoutine?.images || [];
    setImages(newImages);
    imagesRef.current = newImages;
  }, [initialRoutine]);

  useEffect(() => {
    onChange?.(routine as Routine);
  }, [routine]);

  const handleImageUploaded = (response: any) => {
    const raw = response?.data?.[0];
    const file = raw
      ? { id: raw.id, url: raw.fileUrl || raw.url, filename: raw.filename }
      : undefined;
    const newImages = file ? [file] : [];
    setImages(newImages);
    imagesRef.current = newImages;
  };

  const validateForm = async () => {
    try {
      setLoading(true);
      await form?.validateFields();
      const newRoutine = {
        ...routine,
        ...form?.getFieldsValue(),
        images: imagesRef.current,
        routineSteps: routine?.routineSteps || [],
        linkedCause: routine?.linkedCause,
        signNames: routine?.signNames || '',
        routineID: routine?.routineID || 0,
        activeStatus: ActiveStatus.ACTIVE,
        isSuggestion:
          routine?.isSuggestion || (isSuggestion ? YesOrNo.YES : YesOrNo.NO),
        suggestionStatus:
          routine?.suggestionStatus || SuggestionStatus.SUBMITTED,
        ...(isSuggestion &&
          !isSuggestionReview && {
            suggestionSubmitterID: userId,
            suggestionSubmitterName: username,
          }),
        ...(isSuggestionReview && {
          suggestionReviewerID: userId,
          suggestionReviewerName: username,
        }),
      };

      setRoutine(newRoutine);
      return newRoutine;
    } catch (error) {
      console.log(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAddStepClick = async () => {
    const result = await validateForm();
    if (result) {
      setIsStepFormShow(true);
    }
  };

  const handleAddStep = (step: Step) => {
    if (routine) {
      const routineSteps = [...(routine.routineSteps || [])];
      if (currentStep.current >= 0) {
        routineSteps?.splice(currentStep.current, 1, {
          ...routineSteps[currentStep.current],
          ...step,
        });
        setRoutine({
          ...routine,
          routineSteps,
        });
      } else {
        const nextSteps = [
          ...(routine?.routineSteps || []),
          {
            ...step,
            routineID: String(routine.routineID),
            routineStepID: '',
          },
        ];
        setRoutine({
          ...routine,
          routineSteps: nextSteps,
        });
      }
    }
    currentStep.current = -1;
    setIsStepFormShow(false);
  };

  const handleRemoveStep = (index: number) => {
    routine?.routineSteps?.splice(index, 1);
    if (routine) {
      setRoutine({
        ...routine,
        routineSteps: routine?.routineSteps,
      });
    }
  };

  const handleLinkCauses = async (causes: string[]) => {
    setIsLinkCausesDrawerShow(false);
    setRoutine({
      ...routine,
      ...form?.getFieldsValue(),
      linkedCause: availableCauses?.find((c: Cause) =>
        causes.includes(String(c.causeID))
      ),
    });
  };

  const handleUnlinkCause = () => {
    setRoutine({
      ...routine,
      ...form?.getFieldsValue(),
      linkedCause: undefined,
    });
  };

  const handleLinkCausesClick = async () => {
    // const result = await validateForm();
    // if (result) {
    setIsLinkCausesDrawerShow(true);
    // }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const result = await validateForm();

      if (result) {
        const payload = {
          ...routine,
          ...result,
          templateID: templateID || draftTemplate?.templateID,
        };

        const res = await (routine?.routineID
          ? updateRoutine(payload)
          : createRoutine(payload));
        dispatch(
          fetchTemplateRoutines({
            templateId: templateID || draftTemplate?.templateID || '',
          })
        );
        form?.resetFields();
        setImages([]);
        setRoutine(undefined);
        messageApi.open({
          type: 'success',
          content: routine?.routineID
            ? 'Routine Updated!'
            : 'New Routine Created!',
        });
        onSubmit?.(res.data);
      }
    } catch (err) {
      messageApi.open({
        type: 'error',
        content: `Routine ${routine?.routineID ? 'Updated' : 'Created'} Failed!`,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (templateID) {
      dispatch(fetchTemplateCauses({ templateId: templateID }));
    }
  }, [templateID]);

  return (
    <Spin spinning={loading}>
      {contextHolder}
      <Flex vertical gap={16}>
        <Section>
          <Row gutter={[80, 16]}>
            <Col span={8}>
              {readOnly ? (
                <ImageContainer>
                  <Image
                    src={routine?.images?.[0]?.url}
                    preview={!!routine?.images?.[0]?.url}
                    fallback="/placeholder.png"
                  />
                </ImageContainer>
              ) : (
                <ImageUpload
                  fixedWidth={false}
                  businessType={UploadBusinessType.ROUTINE}
                  imageUrl={images?.[0]?.url}
                  onUploaded={handleImageUploaded}
                ></ImageUpload>
              )}
            </Col>
            <Col span={16}>
              <Form.Item
                label="Routine Name"
                name="name"
                rules={[
                  {
                    required: !readOnly,
                    message: 'Please input routine name!',
                  },
                ]}
              >
                {readOnly ? (
                  routine?.name
                ) : (
                  <Input type="text" placeholder="Input Routine Name" />
                )}
              </Form.Item>
              <Form.Item label="Description" name="description">
                {readOnly ? (
                  routine?.description || '-'
                ) : (
                  <TextArea placeholder="Input" rows={5} />
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    Operation And Safety Precautions
                    <span>({parseSignNames(routine?.signNames).length})</span>
                  </span>
                }
                name="signNames"
              >
                <span style={{ display: 'none' }}>{routine?.signNames}</span>
                <SignsField
                  value={routine?.signNames}
                  onChange={
                    readOnly
                      ? undefined
                      : (signNames) =>
                          setRoutine({
                            ...(routine || ({} as Routine)),
                            ...form?.getFieldsValue(),
                            signNames,
                          })
                  }
                  readOnly={readOnly}
                />
              </Form.Item>
            </Col>
          </Row>
        </Section>
        <Section>
          <Flex justify="space-between">
            <SectionBlackTitle>
              Steps<span>({routine?.routineSteps?.length || 0})</span>
            </SectionBlackTitle>
            {!readOnly && (
              <Button type="primary" ghost onClick={handleAddStepClick}>
                Add New Step
              </Button>
            )}
          </Flex>
          {!!routine?.routineSteps?.length && (
            <List>
              {routine?.routineSteps?.map((s, index) => (
                <List.Item key={s?.routineStepID}>
                  <StepCard
                    step={s}
                    onEdit={() => {
                      currentStep.current = index;
                      setIsStepFormShow(true);
                    }}
                    onDelete={() => handleRemoveStep(index)}
                    readOnly={readOnly}
                  />
                </List.Item>
              ))}
            </List>
          )}
        </Section>
        <Section>
          {readOnly ? (
            <SectionBlackTitle>Linked Cause</SectionBlackTitle>
          ) : (
            <>
              <Hint>
                Select the cause you would like to link to this routine
              </Hint>
              <Button type="primary" ghost onClick={handleLinkCausesClick}>
                Link Cause
              </Button>
            </>
          )}
          {!!routine?.linkedCause && (
            <List>
              <List.Item
                key={routine?.linkedCause.causeID}
                actions={
                  !readOnly
                    ? [
                        <Popconfirm
                          key="unlink"
                          placement="topRight"
                          title={`Are you sure you want to unlink ${routine?.linkedCause?.name}`}
                          okText="Yes"
                          cancelText="No"
                          onConfirm={handleUnlinkCause}
                        >
                          <DeleteButton>
                            <Icon component={SVGUnlink} />
                          </DeleteButton>
                        </Popconfirm>,
                      ]
                    : undefined
                }
              >
                <CauseCard cause={routine?.linkedCause} />
              </List.Item>
            </List>
          )}
        </Section>
        <AddStep
          open={isStepFormShow}
          onClose={() => {
            setIsStepFormShow(false);
            currentStep.current = -1;
          }}
          onConfirm={handleAddStep}
          step={routine?.routineSteps?.[currentStep.current as any] as Step}
        ></AddStep>
        {!readOnly && !isSuggestionReview && (
          <Section>
            <Flex justify="flex-end" gap={16}>
              <Button type="primary" ghost onClick={handleCancel}>
                {t('cancel')}
                {isSuggestionReview}
              </Button>
              <Button type="primary" onClick={handleConfirm}>
                {t('confirm')}
              </Button>
            </Flex>
          </Section>
        )}
        {showFullScreenSpin && <Spin spinning={loading} fullscreen />}
        <SelectCauseOrIssueDrawer
          open={isLinkCausesDrawerShow}
          onClose={() => {
            setIsLinkCausesDrawerShow(false);
          }}
          onConfirm={handleLinkCauses}
          data={availableCauses || []}
          singleChoice
        ></SelectCauseOrIssueDrawer>
      </Flex>
    </Spin>
  );
};

export default RoutineForm;
